import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import { Track } from "@/modules/property-tracks/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface TrackSelectionModalProps {
  open: boolean;
  onClose: () => void;
  tracks: Track[];
  selectedTrackUId: string | null;
  onSelectTrack: (trackUId: string) => void;
  onConfirm: () => void;
  isLoading?: boolean;
  title?: string;
}

const TrackSelectionModal = ({
  open,
  onClose,
  tracks,
  selectedTrackUId,
  onSelectTrack,
  onConfirm,
  isLoading = false,
  title = "Notify Resale Admin",
}: TrackSelectionModalProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  const getTrackDisplayName = (track: Track): string => {
    const parts: string[] = [];

    if (track.trackStage?.trackType?.name) {
      parts.push(track.trackStage.trackType.name);
    }

    if (track.property?.compound?.name) {
      parts.push(track.property.compound.name);
    }

    if (track.property?.unitId) {
      parts.push(`Unit ${track.property.unitId}`);
    }

    return parts.length > 0 ? parts.join(" • ") : `Track ${track.uid}`;
  };

  const renderContent = () => (
    <Box
      sx={{
        ...(isMobile
          ? {}
          : {
              padding: theme.nawy.spacing.md,
              maxHeight: "60vh",
              overflowY: "auto",
            }),
      }}
    >
      {isLoading ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={theme.nawy.spacing.md}
          sx={{ py: theme.nawy.spacing.xl }}
        >
          <CircularProgress size={24} />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.secondary,
            }}
          >
            Loading tracks...
          </Typography>
        </Stack>
      ) : tracks.length === 0 ? (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.secondary,
            textAlign: "center",
            py: theme.nawy.spacing.xl,
          }}
        >
          No tracks found for this journey.
        </Typography>
      ) : (
        <FormControl fullWidth>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.secondary,
              py: theme.nawy.spacing.xxxs,
              mb: theme.nawy.spacing.sm,
            }}
          >
            This lead is linked to multiple property tracks, please select which
            property.
          </Typography>
          <Select
            value={selectedTrackUId || ""}
            onChange={(e) => onSelectTrack(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                      color: theme.nawy.colors.semantic.text.secondary,
                    }}
                  >
                    Select a track...
                  </Typography>
                );
              }
              const selectedTrack = tracks.find(
                (track) => track.uid === selected
              );
              return selectedTrack ? getTrackDisplayName(selectedTrack) : "";
            }}
            sx={{
              width: "100%",
              height: "40px",
              borderRadius: theme.nawy.radius.lg,
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.nawy.colors.semantic.border.secondary,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.nawy.colors.semantic.border.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.nawy.colors.semantic.layer.accent,
              },
            }}
          >
            {tracks.map((track) => (
              <MenuItem key={track.uid} value={track.uid}>
                <Box>
                  <Typography
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                      color: theme.nawy.colors.semantic.text.primary,
                    }}
                  >
                    {getTrackDisplayName(track)}
                  </Typography>
                  {track.trackStage?.name && (
                    <Typography
                      sx={{
                        ...theme.nawy.typography.english.variants.body.sm
                          .regular,
                        color: theme.nawy.colors.semantic.text.secondary,
                        mt: theme.nawy.spacing.xxs,
                      }}
                    >
                      Stage: {track.trackStage.name}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title={title}
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        onConfirm={onConfirm}
        onCancel={onClose}
        isLoading={isLoading}
        isConfirmDisabled={!selectedTrackUId || isLoading}
        snapPoints={[0.75, 0.95]}
        initialSnap={0}
      >
        {renderContent()}
      </MobileModal>
    );
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      cancelButtonText="Cancel"
      confirmButtonText="Confirm"
      onConfirm={onConfirm}
      isConfirmDisabled={!selectedTrackUId || isLoading}
      isLoading={isLoading}
      sx={{
        width: "500px",
        maxWidth: "90vw",
        maxHeight: "80vh",
      }}
    >
      {renderContent()}
    </CommonModal>
  );
};

export default TrackSelectionModal;
